// Sampling params (temperature, top_p, top_k, max_tokens, stop).
//
// One set of fields, two homes: the version builder saves them as the
// version's defaults, and the Playground sends them as a one-run override.
// An empty field means "not set" — the provider's own default applies — which
// is how every version behaved before these existed.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import * as React from "react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { updateVersion } from "@/lib/api/endpoints";
import { useVersionLlm } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { GenerationParams } from "@/types/api";

/** Form state: strings so an empty box can mean "unset". */
interface Draft {
  temperature: string;
  top_p: string;
  top_k: string;
  max_tokens: string;
  stop: string;
}

export const toDraft = (p: GenerationParams | null | undefined): Draft => ({
  temperature: p?.temperature?.toString() ?? "",
  top_p: p?.top_p?.toString() ?? "",
  top_k: p?.top_k?.toString() ?? "",
  max_tokens: p?.max_tokens?.toString() ?? "",
  stop: (p?.stop ?? []).join("\n"),
});

/** Draft → params; returns an error message instead when a value is out of range. */
export function fromDraft(d: Draft): { params: GenerationParams | null } | { error: string } {
  const out: GenerationParams = {};
  const num = (raw: string, name: string, min: number, max: number, int: boolean) => {
    if (raw.trim() === "") return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < min || n > max || (int && !Number.isInteger(n))) {
      throw new Error(`${name} must be ${int ? "a whole number " : ""}between ${min} and ${max}.`);
    }
    return n;
  };
  try {
    out.temperature = num(d.temperature, "Temperature", 0, 2, false);
    out.top_p = num(d.top_p, "Top P", 0, 1, false);
    out.top_k = num(d.top_k, "Top K", 1, 500, true);
    out.max_tokens = num(d.max_tokens, "Max tokens", 1, 200_000, true);
  } catch (e) {
    return { error: (e as Error).message };
  }
  const stop = d.stop
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  if (stop.length > 4) return { error: "At most 4 stop sequences." };
  if (stop.length) out.stop = stop;
  const set = Object.fromEntries(
    Object.entries(out).filter(([, v]) => v !== undefined),
  ) as GenerationParams;
  return { params: Object.keys(set).length ? set : null };
}

export function GenerationFields({
  draft,
  onChange,
  provider,
  compact = false,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
  /** The version's provider; OpenAI has no top_k, so the box is disabled. */
  provider?: string | null;
  compact?: boolean;
}) {
  const noTopK = provider === "openai";
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange({ ...draft, [k]: e.target.value });
  const field = (k: keyof Draft, label: string, hint: string, disabled = false) => (
    <div className="space-y-1">
      <Label htmlFor={`gen-${k}`}>{label}</Label>
      <Input
        id={`gen-${k}`}
        inputMode="decimal"
        placeholder="default"
        value={disabled ? "" : draft[k]}
        onChange={set(k)}
        disabled={disabled}
      />
      {!compact && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
  return (
    <div className="space-y-3">
      <div className={compact ? "grid grid-cols-2 gap-3" : "grid gap-4 sm:grid-cols-2"}>
        {field("temperature", "Temperature", "0–2. Lower = more deterministic.")}
        {field("top_p", "Top P", "0–1. Nucleus sampling cutoff.")}
        {field(
          "top_k",
          "Top K",
          noTopK ? "Not supported by OpenAI models." : "1–500. Anthropic and Gemini only.",
          noTopK,
        )}
        {field("max_tokens", "Max tokens", "Cap on the reply length.")}
      </div>
      <div className="space-y-1">
        <Label htmlFor="gen-stop">Stop sequences</Label>
        <textarea
          id="gen-stop"
          className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
          placeholder="one per line (max 4)"
          value={draft.stop}
          onChange={set("stop")}
        />
      </div>
    </div>
  );
}

/** Version-builder card: saves the params as this version's defaults. */
export function GenerationParamsCard({
  agentSlug,
  label,
  current,
}: {
  agentSlug: string;
  label: string;
  current: GenerationParams | null;
}) {
  const qc = useQueryClient();
  const llm = useVersionLlm(agentSlug, label);
  const [draft, setDraft] = React.useState<Draft>(() => toDraft(current));
  React.useEffect(() => setDraft(toDraft(current)), [current]);

  const save = useMutation({
    mutationFn: (params: GenerationParams | null) =>
      updateVersion(agentSlug, label, { generation_params: params }),
    onSuccess: async () => {
      toast.success("Generation settings saved.");
      await qc.invalidateQueries({ queryKey: qk.version(agentSlug, label) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submit = () => {
    const parsed = fromDraft(draft);
    if ("error" in parsed) return toast.error(parsed.error);
    save.mutate(parsed.params);
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <SlidersHorizontal className="h-4 w-4 text-primary" />
        <CardTitle className="text-base">Generation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Leave a field empty to use the model&apos;s default. The Playground can override these for
          a single run.
        </p>
        <GenerationFields draft={draft} onChange={setDraft} provider={llm.data?.provider} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDraft(toDraft(null))}>
            Reset to defaults
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save generation"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
